// app/page/default/alternative-dashboard/useDashboardLayoutData.ts
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { AppDispatch, RootState } from "@/app/redux/store";
import {
  setDashboardLayoutView,
  setDashboardLayoutLoading,
  setDashboardLayoutError,
} from "@/app/redux/reducers/dashboardLayoutSlice";
import { gxDashboardLayoutViewApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { gxIsArray } from "@/app/utils/helpers";
import { ROUTE_TO_LAYOUT_ID } from "@/app/router/dashboardLayoutRoutes.config";
import type {
  DashboardLayoutViewPayload,
  DashboardLayoutViewResponse,
} from "@/app/types/alterNativeDashboardLayout";

export const useDashboardLayoutData = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { layoutId, menuDetail, loading } = useSelector(
    (state: RootState) => state.dashboardLayout,
  );

  const expectedLayoutId = ROUTE_TO_LAYOUT_ID[location.pathname];

  useEffect(() => {
    if (!expectedLayoutId) return;

    if (layoutId === expectedLayoutId && menuDetail.length > 0) return;

    let isMounted = true;

    const fetchLayoutView = async () => {
      dispatch(setDashboardLayoutLoading(true));
      try {
        const payload: DashboardLayoutViewPayload = {
          layoutId: expectedLayoutId,
        };
        const result = await gxDashboardLayoutViewApi(payload);
        if (!isMounted) return;

        const viewData = (
          gxIsArray(result?.data) ? result.data : []
        ) as DashboardLayoutViewResponse[];

        const layoutView = viewData[0];

        if (layoutView) {
          dispatch(
            setDashboardLayoutView({
              layoutId: layoutView.layoutId,
              layoutName: layoutView.layoutName,
              menuDetail: layoutView.menuDetail ?? [],
            }),
          );
        }
      } catch (err) {
        if (isMounted) {
          dispatch(setDashboardLayoutError("Failed to load dashboard layout"));
        }
      } finally {
        if (isMounted) dispatch(setDashboardLayoutLoading(false));
      }
    };

    fetchLayoutView();

    return () => {
      isMounted = false;
    };
  }, [expectedLayoutId]);

  return { layoutId, menuDetail, loading };
};
